// The body of a number's tooltip: its factor list grouped by source namespace (Section 22,
// 24.5), then the result. Base rows read "= 40", multipliers "x1.20", additions "+5.0".
import type { FactorGroup, FactorRow, TooltipVM } from '../../viewModels';
import { formatNum, humanize, oneDecimal, signed, type NumFormat } from '../format';
import { uiText } from '../text/en';

function opText(row: FactorRow): string {
  switch (row.op) {
    case 'base':
    case 'set':
      return `= ${oneDecimal(row.value)}`;
    case 'add':
      return signed(row.value);
    case 'mul':
      return `x${(Math.round(row.value * 100) / 100).toFixed(2)}`;
    case 'min':
      return `at most ${oneDecimal(row.value)}`;
    case 'max':
      return `at least ${oneDecimal(row.value)}`;
  }
}

function rowLabel(row: FactorRow): string {
  if (row.namespace === 'flow') return uiText.flow[row.id] ?? humanize(row.id);
  if (row.namespace === 'seed') return uiText.seed[row.id] ?? humanize(row.id);
  if (row.namespace === 'population') return uiText.populationFlow[row.id] ?? humanize(row.id);
  if (row.op === 'base') return uiText.namespace.base ?? 'Base';
  return row.note ?? humanize(row.id);
}

function groupLabel(group: FactorGroup): string {
  return uiText.namespace[group.namespace] ?? humanize(group.namespace);
}

export function FactorList({
  vm,
  format = 'one',
  showResult = true,
}: {
  vm: TooltipVM;
  format?: NumFormat;
  showResult?: boolean;
}) {
  if (vm.groups.length === 0) return <p class="factors__none">{uiText.tooltip.none}</p>;
  return (
    <table class="factors">
      {vm.groups.map((g) => (
        <tbody key={g.namespace}>
          {g.namespace !== 'base' && (
            <tr class="factors__group">
              <th colSpan={2} scope="rowgroup">
                {groupLabel(g)}
              </th>
            </tr>
          )}
          {g.rows.map((r, i) => (
            <tr key={`${r.source}-${i}`} class={r.delta < 0 ? 'factors__row is-negative' : 'factors__row'}>
              <th scope="row">{rowLabel(r)}</th>
              <td class="num">{opText(r)}</td>
            </tr>
          ))}
        </tbody>
      ))}
      {showResult && (
        <tfoot>
          <tr class="factors__result">
            <th scope="row">{uiText.tooltip.result}</th>
            <td class="num">{formatNum(vm.value, format)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}
